// Captura de gráficas DevExtreme a PNG, reutilizando la instancia ya renderizada
// en pantalla (sin re-montar ni re-consultar el backend). Es la vía más barata
// para incrustar la gráfica en PDF/Excel.

export interface CapturedImage {
  dataUrl: string; // data:image/jpeg;base64,...
  width: number; // px del SVG original
  height: number;
  format: 'JPEG' | 'PNG';
}

// Convierte un markup SVG (el que devuelve chartInstance.svg()) a un dataURL de imagen.
// Por defecto JPEG: jsPDF arma el PDF como un único string en memoria y los PNG grandes
// lo hacen reventar con "Invalid string length". JPEG es mucho más liviano.
const svgToImage = (svgMarkup: string, width: number, height: number, scale = 1.5, quality = 0.85): Promise<string> =>
  new Promise((resolve, reject) => {
    try {
      // Asegurar namespace (algunos navegadores lo requieren para Image)
      let markup = svgMarkup;
      if (!/xmlns=/.test(markup)) {
        markup = markup.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
      }
      const svgBlob = new Blob([markup], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(width * scale);
          canvas.height = Math.round(height * scale);
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            URL.revokeObjectURL(url);
            reject(new Error('No 2D context'));
            return;
          }
          // Fondo blanco obligatorio para JPEG (no soporta transparencia)
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.scale(scale, scale);
          ctx.drawImage(img, 0, 0, width, height);
          URL.revokeObjectURL(url);
          resolve(canvas.toDataURL('image/jpeg', quality));
        } catch (err) {
          URL.revokeObjectURL(url);
          reject(err);
        }
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };
      img.src = url;
    } catch (err) {
      reject(err);
    }
  });

// Captura una instancia de chart DevExtreme a imagen (JPEG).
export const chartInstanceToPng = async (instance: any, scale = 1.5): Promise<CapturedImage | null> => {
  try {
    if (!instance || typeof instance.svg !== 'function') return null;
    const markup: string = instance.svg();
    if (!markup) return null;
    let width = 600;
    let height = 360;
    if (typeof instance.getSize === 'function') {
      const sz = instance.getSize();
      if (sz && sz.width) width = sz.width;
      if (sz && sz.height) height = sz.height;
    }
    const dataUrl = await svgToImage(markup, width, height, scale);
    return { dataUrl, width, height, format: 'JPEG' };
  } catch {
    return null;
  }
};

// Captura un mapa de instancias (clave = ConfiguracionId) a imágenes PNG.
export const captureChartImages = async (instances: Map<number, any>, scale = 1.5): Promise<Map<number, CapturedImage>> => {
  const result = new Map<number, CapturedImage>();
  const tasks: Array<Promise<void>> = [];
  instances.forEach((inst, id) => {
    tasks.push(
      chartInstanceToPng(inst, scale).then((img) => {
        if (img) result.set(id, img);
      })
    );
  });
  await Promise.all(tasks);
  return result;
};
